import axios from 'axios';
import { readShopProduct } from './shopProduct';
import { sharedGet } from './sharedGet';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { cleanListingUrl } from './listingUrl';
import { withMargin } from './cropMargin';
import { asHeroBox, type HeroBox } from './realImages';
import { type MaterialPair } from './variantLabel';
import { DIRECTION_IDS, OTHER_DIRECTION } from './viewDirections';
import { haltGate } from './runHalt';

// The product's web listings — one per colour/material "submodel" of the same model.
//
// There is no privileged "primary" listing: every link is an equal submodel, read on
// its own, and each contributes its materials, its own photo gallery, and its own
// finished-product picture. The same manual routinely covers several listings that
// differ only in what they are made of; this is the list of them.
//
// The user pastes the links in Phase 1; a run reads each one in parallel with the manual
// (see runListing / runAll). Where the manual and the listings disagree on the shared
// fact (product name) the user picks — see infoPicks, which reads the shared
// facts through a single REPRESENTATIVE view of the list (the first submodel that
// answered), since every submodel names the same product.
//
// Each submodel's found photos are saved to the notice keyed by its ID, so the Product
// photos table (and the public tutorial's colour picker) can group by it — and go on
// grouping by it when a re-read rewords the materials. The label rides along for display.
//
// Module scope + shared document state, like every other notice store, so the answers
// survive leaving the tab — and reach the other admin.

export type ListingInfo = {
  // What the user pasted, cleaned of its tracking tail. '' means "empty row".
  url: string;
  productName: string;
  // What this listing says the product is made of: one (material, colour) pair per
  // substance, in the order the page names them, either half possibly empty. This is what
  // tells a submodel apart on screen — the Phase 1 info table (a row per pair) and the
  // label its photos are captioned with (see variantLabel). It is NOT what identifies it:
  // that is `id`, which does not move when a re-read rewords a colour.
  materials: MaterialPair[];
  // Every picture the listing shows, as URLs on the merchant's CDN, og:image first,
  // minus the ones a model judged show no product (a fabric swatch, a colour chip).
  gallery: string[];
  // Where the gallery on screen came from: 'db' when adopted from the stored set,
  // 'search' when a fresh read, '' when unknown.
  gallerySource: '' | 'db' | 'search';
  // Which side of the product each gallery picture shows, one per entry of `gallery` and
  // in the same order: a direction a multi-view reconstructor names, or 'other'. Phase 1
  // classifies them so the 3D pipeline can drop a real photograph straight into the slot
  // that wants it, instead of somebody eyeballing nine thumbnails per submodel.
  //
  // Empty when the read predates the classification, which reads as "nobody has looked"
  // rather than as "none of them is a front view".
  galleryViews: string[];
  // Which of those pictures carry MEASUREMENTS drawn on them: leader lines with a number
  // and a unit. Parallel to `gallery`, same length, from the same call that reads the
  // directions — it is already looking at every picture, so the flag is free.
  //
  // These are where a part's real size comes from. The manual gives "M6 x 70mm" for a
  // bolt and nothing for a seat; the listing's dimension drawing gives 48,2 cm across the
  // backrest and 73,3 cm overall.
  galleryDimensioned: boolean[];
  // The picture this submodel publishes as its splash, and WHAT IS CUT out of it (0..1
  // from the top-left, margin already added — see cropMargin). heroUrl is one of
  // `gallery`; '' when the listing has none.
  heroUrl: string;
  heroBox: HeroBox | null;
  // What the model picked, kept beside what the user did with it, so "undo" needn't
  // re-pay for the call. They move only on a fresh read.
  modelHeroUrl: string;
  modelHeroBox: HeroBox | null;
  reasoning: string;
  raw: string;
  // Why the last read failed, shown next to the field. A listing that 404s or blocks the
  // reader is a normal outcome, not a pipeline failure.
  error: string;
};

// One submodel: a listing's answers plus a stable local id (for React keys / removal —
// the URL is empty while a row is typed, and two blank rows must stay distinct), a live
// "reading now" flag, and a "queued to be re-read this run" flag. `pending` is set for
// every link a run will touch, up front, so a row about to be re-read shows its spinner
// immediately rather than its stale value while it waits its serial turn.
export type Submodel = ListingInfo & { id: string; detecting: boolean; pending: boolean };

// The representative view the shared-fact machinery reads: a single ListingInfo standing
// in for the whole list. Its product name is the first non-empty across submodels
// (they all name the same product), its gallery/hero are the first submodel's (the
// default published splash), and it is "detecting" while ANY submodel reads.
export type RepListing = ListingInfo & { detecting: boolean };

const emptyInfo = (): ListingInfo => ({
  url: '', productName: '', materials: [],
  gallery: [], gallerySource: '', galleryViews: [], galleryDimensioned: [],
  heroUrl: '', heroBox: null,
  modelHeroUrl: '', modelHeroBox: null,
  reasoning: '', raw: '', error: '',
});

const newId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `s-${Math.random().toString(36).slice(2)}`;

// What a document persisted, or a listing read answered, BEFORE the materials were a
// list: one wood and one seat, the only two a chair has.
type LegacyAttrs = { wood?: string; seatMaterial?: string; seatColor?: string };

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

// The materials out of whatever shape they arrive in, dropping the pairs that name
// neither half.
//
// Failing that, the pre-list fields, converted IN THE ORDER THE OLD LABEL JOINED THEM —
// seat first, then wood — so a migrated document's captions read as they always did.
function coerceMaterials(o: (Partial<ListingInfo> & LegacyAttrs) | null | undefined): MaterialPair[] {
  const pairs = Array.isArray(o?.materials)
    ? o!.materials.map((p) => ({ material: str(p?.material), color: str(p?.color) }))
    : [
        { material: str(o?.seatMaterial), color: str(o?.seatColor) },
        { material: str(o?.wood), color: '' },
      ];
  return pairs.filter((p) => p.material || p.color);
}

// One submodel out of whatever was persisted — the same guards the single listing used
// (a gallery is a list of strings; a hero must be one of this submodel's pictures).
function coerceInfo(o: (Partial<ListingInfo> & LegacyAttrs) | null | undefined): ListingInfo {
  const gal = Array.isArray(o?.gallery)
    ? o!.gallery.filter((u): u is string => typeof u === 'string' && !!u)
    : [];
  return {
    url: o?.url || '',
    productName: o?.productName || '',
    materials: coerceMaterials(o),
    gallery: gal,
    gallerySource: o?.gallerySource === 'db' || o?.gallerySource === 'search' ? o.gallerySource : '',
    // Trimmed to the gallery it describes: a stored classification outlives the strip it
    // was made from, and a re-read can return fewer pictures than it judged.
    galleryViews: Array.isArray(o?.galleryViews)
      ? o!.galleryViews.slice(0, gal.length).map((v) => (typeof v === 'string' ? v : 'other'))
      : [],
    galleryDimensioned: Array.isArray(o?.galleryDimensioned)
      ? o!.galleryDimensioned.slice(0, gal.length).map((v) => v === true)
      : [],
    // A hero that is not one of this submodel's pictures is not a hero: a stored pick
    // outlives the strip it was made from, and a re-run can drop the picture it named.
    heroUrl: gal.includes(o?.heroUrl || '') ? (o!.heroUrl as string) : (gal[0] ?? ''),
    heroBox: asHeroBox(o?.heroBox),
    modelHeroUrl: gal.includes(o?.modelHeroUrl || '') ? (o!.modelHeroUrl as string) : '',
    modelHeroBox: asHeroBox(o?.modelHeroBox),
    reasoning: o?.reasoning || '',
    raw: o?.raw || '',
    error: o?.error || '',
  };
}

const coerceSubmodel = (o: (Partial<Submodel> & LegacyAttrs) | null | undefined): Submodel => ({
  ...coerceInfo(o),
  id: (o?.id && String(o.id)) || newId(),
  detecting: false,
  pending: false,
});

// A submodel is stored as TWO keys, because it is two different kinds of thing.
//
// The LINKS are input — what the user pasted in Phase 1. They belong to the manual the
// way the PDF does: nobody reads them, no run produces them, and the same ones hold
// whichever run is on screen. So they go under a DOC-SCOPED key (the server files
// `notice.links.` under the document, not under a run — see database.DOC_RUN_ID), which
// also means re-running a phase, or wiping a phase's answers, leaves them standing.
//
// The ANSWERS are what a run read off those pages, keyed BY LINK ID, so they stay
// per-run: run 12 read a beige seat, run 13 reads it again, and looking at run 12 shows
// what run 12 found.
const linksKey = (docId: string) => `notice.links.${docId}`;
const answersKey = (docId: string) => `notice.submodels.${docId}`;
// The pre-split key: one array of link-plus-answers per run. Read once to migrate.
// Older still: a single primary listing and a lighter list of extra variants.
const legacyListingKey = (docId: string) => `notice.listing.${docId}`;
const legacyVariantsKey = (docId: string) => `notice.variants.${docId}`;

type StoredLink = { id: string; url: string };
// The answers of one link, by its id. Everything a run found except the url, which is
// the link's and not the answer's.
type StoredAnswers = Record<string, Partial<ListingInfo> & LegacyAttrs>;

// Every submodel the OLD single-array shape holds, for a document that has not been
// through the split yet — first this run's array, then the pre-unification pair.
function loadLegacy(docId: string): Submodel[] {
  type LegacyInfo = Partial<ListingInfo> & LegacyAttrs;
  const arr = readState<(Partial<Submodel> & LegacyAttrs)[] | null>(docId, answersKey(docId), null);
  if (Array.isArray(arr)) return arr.map(coerceSubmodel);
  // Nothing is lost — a run refills what the lighter variants lacked.
  const legacy: Submodel[] = [];
  const primary = readState<LegacyInfo | null>(docId, legacyListingKey(docId), null);
  if (primary && (primary.url || primary.raw || (primary.gallery || []).length)) {
    legacy.push(coerceSubmodel(primary));
  }
  const variants = readState<LegacyInfo[] | null>(docId, legacyVariantsKey(docId), null);
  if (Array.isArray(variants)) legacy.push(...variants.map(coerceSubmodel));
  return legacy;
}

function load(docId: string): Submodel[] {
  try {
    const links = readState<StoredLink[] | null>(docId, linksKey(docId), null);
    if (!Array.isArray(links)) return loadLegacy(docId);
    // The links are the list: their order is the rows on screen, and an answer whose
    // link is gone is an answer about nothing. A link with no answer yet is a row
    // waiting to be read, which is exactly what a fresh run is.
    const answers = readState<StoredAnswers>(docId, answersKey(docId), {});
    return links
      .filter((l) => l && typeof l.id === 'string')
      .map((l) => coerceSubmodel({ ...(answers?.[l.id] || {}), id: l.id, url: l.url || '' }));
  } catch {
    return [];
  }
}

type Snapshot = { submodels: Submodel[] };

const store = createDocEntryStore<Snapshot>((docId) => ({ snapshot: { submodels: load(docId) } }));

function persist(docId: string) {
  const subs = store.getEntry(docId).snapshot.submodels;
  writeState(docId, linksKey(docId), subs.map(({ id, url }) => ({ id, url })));
  const answers: StoredAnswers = {};
  // The url is not written twice: it is the link's, and a second copy here is a second
  // answer to "which page is this" that could disagree with the first.
  for (const { detecting: _d, pending: _p, id, url: _u, ...answer } of subs) answers[id] = answer;
  writeState(docId, answersKey(docId), answers);
}

// Replace the whole list (a new reference, so subscribers re-render) and persist.
function setSubmodels(docId: string, submodels: Submodel[]) {
  store.patch(docId, { submodels });
  persist(docId);
}

// Merge a partial into one submodel by id, leaving the rest untouched.
function patchSubmodel(docId: string, id: string, partial: Partial<Submodel>) {
  const submodels = store.getEntry(docId).snapshot.submodels.map((s) =>
    s.id === id ? { ...s, ...partial } : s,
  );
  setSubmodels(docId, submodels);
}

// --- Representative view (the shared facts + default splash) ------------------
const firstNonEmpty = (subs: Submodel[], f: keyof ListingInfo): string =>
  (subs.find((s) => typeof s[f] === 'string' && (s[f] as string).trim())?.[f] as string) || '';

function representative(subs: Submodel[]): RepListing {
  if (!subs.length) return { ...emptyInfo(), detecting: false };
  return {
    // Gallery / hero / prose come from the first submodel (the default splash + the
    // gallery the Phase 1 picker shows).
    ...subs[0],
    // The shared facts are the first submodel that actually named them — every submodel
    // is the same product, so this just skips a blank first row.
    productName: firstNonEmpty(subs, 'productName'),
    detecting: subs.some((s) => s.detecting),
  };
}

// --- Reads -------------------------------------------------------------------
// A whether-it-has-been-read test: a submodel with any answer has had its listing read,
// so a resumed run must not pay for it again.
const isRead = (s: Submodel): boolean =>
  !!s.raw.trim() || s.gallery.length > 0 || s.materials.length > 0;

// Adopt the pictures the DATABASE holds for this notice's submodel label, so an empty
// read never blanks a gallery a prior run found. Returns true when there were some.
async function storedGallery(docId: string, id: string): Promise<string[]> {
  try {
    const { data } = await sharedGet<{ images: { id: number; url: string; variant?: string }[] }>(
      `/api/notice/listing-images/${encodeURIComponent(docId)}`,
    );
    // Matched on the submodel's id, which is what the photos were filed under — and what
    // does not move when a re-read rewords this listing's materials.
    return (data.images || [])
      .filter((i) => i.url && (i.variant || '') === id)
      .map((i) => i.url);
  } catch {
    return [];
  }
}

// Ask what each picture of a strip is a view of. The fresh read answers this for itself
// (its own call, on the pictures it just found); this is for every strip that reaches the
// screen any other way — adopted from the database, carried forward from an earlier run,
// or found by a read whose own classification failed.
//
// Fire-and-forget: the pictures are already on screen and the directions land a few
// seconds later, so nothing waits. It patches by `id` rather than by closure, so a read
// that has moved on since is not overwritten with an answer about the old strip.
async function classifyGallery(docId: string, id: string, urls: string[], productName: string) {
  try {
    const { data } = await axios.post<{ galleryViews?: string[]; galleryDimensioned?: boolean[] }>(
      '/api/notice/gallery-views', { urls, productName },
    );
    const views = Array.isArray(data.galleryViews) ? data.galleryViews : [];
    const dimensioned = Array.isArray(data.galleryDimensioned) ? data.galleryDimensioned : [];
    // Only if this submodel is still showing the very pictures we asked about — a
    // re-search between the two moments makes these directions somebody else's.
    const now = store.get(docId).submodels.find((x) => x.id === id);
    if (!now || now.gallery.length !== urls.length) return;
    if (now.gallery.some((u, i) => u !== urls[i])) return;
    patchSubmodel(docId, id, { galleryViews: views, galleryDimensioned: dimensioned });
  } catch {
    /* no directions is the state it was already in */
  }
}

// Read ONE submodel's listing: the product name / materials / gallery / hero,
// all from its own page ("one search per link"). Its photos are saved under its submodel
// label so the Product photos table and the public picker can group them.
export async function runSubmodel(
  docId: string, id: string, t: (k: string) => string, resume = false, force = false,
) {
  const s = store.get(docId).submodels.find((x) => x.id === id);
  if (!s || !s.url || s.detecting) return;
  // A resumed run re-asks for nothing already read; a fresh run reads every link.
  if (!force && resume && isRead(s)) return;
  // Reading now: clear the queued flag and any stale error.
  patchSubmodel(docId, id, { detecting: true, pending: false, error: '' });
  try {
    // The shop's product record, read by this browser when the shop allows it (see
    // shopProduct): a shop that blocks the server is still read this way.
    const product = await readShopProduct(s.url);
    const { data } = await axios.post<Partial<ListingInfo> & LegacyAttrs>(
      '/api/notice/listing', { url: s.url, ...(product ? { product } : {}) },
    );
    const modelBox = asHeroBox(data.heroBox);
    const heroBox = modelBox && withMargin(modelBox);
    const gallery = Array.isArray(data.gallery) ? data.gallery.filter(Boolean) : [];
    const shared = {
      productName: (data.productName || '').trim(),
      materials: coerceMaterials(data),
      reasoning: data.reasoning || '',
      raw: data.raw || '',
      error: '',
    };
    if (!gallery.length) {
      // Empty read, and exactly ONE thing may survive it: the pictures the DATABASE holds
      // for this submodel, ADOPTED into the strip (`gallerySource: 'db'`).
      //
      // What was already on screen does NOT. It used to win this contest, on the grounds
      // that it was this notice's own answer to this link — but "this notice's own answer"
      // is a flattering name for the previous run's leftovers, and it made a re-read
      // unable to change anything. A strip carried forward is a strip nothing new is ever
      // learned about: it never arrives, so it is never re-classified, and a run asked to
      // read the listing again would report success having read nothing.
      //
      // So the strip now says what THIS read found, or what the database holds, or
      // nothing. Empty is a real outcome and is allowed to show.
      const stored = await storedGallery(docId, id);
      patchSubmodel(docId, id, {
        ...shared,
        ...(stored.length ? {
          gallery: stored,
          gallerySource: 'db' as const,
          // Cleared, not carried: the last read's directions describe the strip IT
          // found, and these are different pictures. They are asked for below, once
          // the adopted strip is the one on screen.
          galleryViews: [],
          galleryDimensioned: [],
        } : {
          gallery: [], gallerySource: '' as const,
          galleryViews: [], galleryDimensioned: [],
          heroUrl: '', heroBox: null,
          modelHeroUrl: '', modelHeroBox: null,
        }),
      });
      return;
    }
    // The gallery is NOT saved to the notice's photo database here — a read only shows
    // what it found (the strip lives in the submodel's own state and survives via
    // docState). A picture enters the Product photos database only when the user toggles
    // it in, per thumbnail (see ListingGallery's DB switch). Nothing is added by default.
    patchSubmodel(docId, id, {
      ...shared,
      gallery,
      gallerySource: 'search',
      galleryViews: Array.isArray(data.galleryViews) ? data.galleryViews : [],
      galleryDimensioned: Array.isArray(data.galleryDimensioned) ? data.galleryDimensioned : [],
      heroUrl: data.heroUrl || '',
      heroBox,
      modelHeroUrl: data.heroUrl || '',
      modelHeroBox: heroBox,
    });
  } catch (e) {
    const msg = axios.isAxiosError(e) ? e.response?.data?.error : '';
    patchSubmodel(docId, id, { error: msg || t('notice.listing.failed') });
  } finally {
    patchSubmodel(docId, id, { detecting: false });
    // Whatever the read just did — found a strip, adopted the database's, kept the one
    // already on screen, or failed outright — the strip now showing either carries its
    // directions or does not, and that is the only thing worth testing.
    //
    // This used to hang off "we just adopted from the database", which asked how the
    // pictures ARRIVED rather than what state they are in. A strip adopted by an earlier
    // run and carried forward into this one never arrives again, so it was never asked
    // about and never could be. The same shape of mistake as the storedGallery bug above,
    // one level up.
    // Also when the directions are there but the DIMENSIONED flags are not. That is a
    // strip classified before the flag existed: the pictures were judged, so the test
    // above is satisfied, and the one thing the classification is now also for would stay
    // missing for the life of the notice. One extra call, once, per submodel that predates
    // the question — and without it a listing whose dimension drawing is sitting in the
    // strip reports "no dimension drawing found".
    const now = store.get(docId).submodels.find((x) => x.id === id);
    if (now && now.gallery.length
        && (!now.galleryViews.length || !now.galleryDimensioned.length)) {
      void classifyGallery(docId, id, now.gallery, now.productName);
    }
  }
}

// Read every submodel that has a URL — one at a time, NOT in parallel. Several concurrent
// reads of the same merchant domain get rate-limited, and a throttled read comes back with
// a truncated gallery (one image instead of the full strip). Reading them in series keeps
// each link's gallery complete, at the cost of the reads no longer overlapping. This is
// what the run pipeline and the Phase-2 Lancer call (compat name kept: it used to read the
// one listing).
export async function runListing(docId: string, t: (k: string) => string, resume = false) {
  const subs = store.get(docId).submodels;
  // Only the links this run will actually read: those with a URL, minus the ones a resumed
  // run skips (already read). A resumed skip must NOT be queued, or its spinner never ends.
  const ids = subs.filter((s) => s.url && !(resume && isRead(s))).map((s) => s.id);
  if (ids.length) {
    const queued = new Set(ids);
    // EVERY ERROR FROM THE LAST RUN GOES NOW, not when each link's own turn comes round.
    //
    // The reads are serial, so a message cleared at the top of runSubmodel stays on screen
    // for as long as the links before it take — a failure being reported by a page that is
    // already busy replacing it. And a link the run skips (its URL removed since) kept its
    // message for good, explaining a read of an address that is no longer there.
    //
    // Only the errors. The materials, galleries and heroes stay: they are the last good
    // answers, and a phase that emptied itself on launch would lose a settled answer to
    // announce an unfinished one.
    setSubmodels(docId, subs.map((s) => (
      queued.has(s.id) ? { ...s, pending: true, error: '' } : { ...s, error: '' }
    )));
  }
  // STOP READS NO NEXT LINK (see runHalt): the one out lands and is kept, and the links it never
  // reached stop saying they are queued.
  const halted = haltGate(docId);
  for (const [n, id] of ids.entries()) {
    if (halted()) {
      const left = new Set(ids.slice(n));
      setSubmodels(docId, store.get(docId).submodels.map((s) => (left.has(s.id) ? { ...s, pending: false } : s)));
      break;
    }
    await runSubmodel(docId, id, t, resume);
  }
}

// One submodel's gallery "Re-run search" button: re-read that link on demand.
export const researchSubmodel = (docId: string, id: string, t: (k: string) => string) =>
  runSubmodel(docId, id, t, false, true);

// --- List editing (Phase 1 field) -------------------------------------------
export function addSubmodel(docId: string) {
  setSubmodels(docId, [...store.get(docId).submodels, coerceSubmodel({ id: newId() })]);
}

// Add a link somebody did not type: one the server found for this manual and verified
// (see listingSuggest). Nothing is read here — accepting a suggestion leaves it in
// exactly the state a pasted link is in, waiting for the next run.
//
// It FILLS THE EMPTY ROW rather than always appending, because the field keeps one
// blank row to paste into: appending under it would leave a hole in the middle of the
// list on the way to four links. A link already in the list is not added twice — the
// suggestions are matched against it on screen, and this is the same rule enforced
// where it counts.
export function addSubmodelUrl(docId: string, raw: string) {
  const url = cleanListingUrl(raw);
  if (!url) return;
  const subs = store.get(docId).submodels;
  if (subs.some((s) => s.url === url)) return;
  const blank = subs.find((s) => !s.url);
  if (blank) {
    patchSubmodel(docId, blank.id, { url });
    return;
  }
  setSubmodels(docId, [...subs, coerceSubmodel({ id: newId(), url })]);
}

export function removeSubmodel(docId: string, id: string) {
  // Only the input is removed; any photos it saved stay in the Product photos database
  // (prune them there). Wiping them here would delete another admin's curation.
  setSubmodels(docId, store.get(docId).submodels.filter((s) => s.id !== id));
}

// A submodel's URL. Cleaned of its tracking tail. A changed link is a different submodel,
// so its old answers are dropped — nothing stale shows while it is (re)read.
export function setSubmodelUrl(docId: string, id: string, raw: string) {
  const url = cleanListingUrl(raw);
  const cur = store.get(docId).submodels.find((s) => s.id === id);
  if (!cur || url === cur.url) return;
  patchSubmodel(docId, id, { ...emptyInfo(), id, detecting: false, pending: false, url });
}

// --- Hero box (per submodel, dragged in the zoom) -------------------------------
export function setSubmodelHeroBox(docId: string, id: string, heroBox: HeroBox | null) {
  if (store.get(docId).submodels.some((x) => x.id === id)) patchSubmodel(docId, id, { heroBox });
}

// Overrule the classifier on one picture. Left and right of a symmetric object are the
// one call it still gets wrong sometimes, and a wrong direction is not a wrong caption:
// it is what a reconstructor will be TOLD the picture is. So it has to be correctable,
// and the correction has to be the last word — nothing re-asks, exactly as with the
// splash and its crop.
//
// The array is padded to the strip's length rather than written sparsely, so a picture
// nobody has judged reads as 'other' (looked at, belongs to no direction) instead of as a
// hole that indexes past it.
export function setGalleryView(docId: string, id: string, index: number, view: string) {
  const s = store.get(docId).submodels.find((x) => x.id === id);
  if (!s || index < 0 || index >= s.gallery.length) return;
  if (!DIRECTION_IDS.includes(view)) return;
  const galleryViews = s.gallery.map((_, i) => s.galleryViews[i] ?? OTHER_DIRECTION);
  galleryViews[index] = view;
  patchSubmodel(docId, id, { galleryViews });
}

// --- Reads (hooks + getters) -------------------------------------------------
// The representative view — product name / gallery / hero / detecting — for the
// shared-fact machinery (infoPicks), the Phase 1 gallery, the splash and phase progress.
export const getListing = (docId: string): RepListing => representative(store.get(docId).submodels);
export function useListing(docId: string): RepListing {
  const { submodels } = store.useSnapshot(docId);
  return representative(submodels);
}

// The full list — for the Phase 1 link editor and the Phase 1 info table (one row each).
export const getSubmodels = (docId: string): Submodel[] => store.get(docId).submodels;
export const useSubmodels = (docId: string): Submodel[] => store.useSnapshot(docId).submodels;

// THE COLOURWAYS: the link rows with a link in them (Louis 2026-11-03). A row left empty is a
// field to paste into, not a colourway: counted as one, it was a colourway with no photograph
// and no material in every phase that lists them, and an empty row cannot be removed. When no
// row has a link, the first is the notice's only colourway, which is what it always was.
export const isColourway = (subs: Submodel[], s: Submodel): boolean =>
  !!s.url || (!subs.some((one) => one.url) && s.id === subs[0]?.id);
export const colourwaysOf = (subs: Submodel[]): Submodel[] => subs.filter((s) => isColourway(subs, s));
export const getColourways = (docId: string): Submodel[] => colourwaysOf(getSubmodels(docId));

// One submodel by id — for its own Phase 1 gallery. A blank stand-in when the id is gone
// (a row removed mid-render) so the gallery unmounts cleanly rather than crashing.
const blankSubmodel = (id: string): Submodel => ({ ...emptyInfo(), id, detecting: false, pending: false });
export const getSubmodel = (docId: string, id: string): Submodel =>
  store.get(docId).submodels.find((s) => s.id === id) ?? blankSubmodel(id);
export function useSubmodel(docId: string, id: string): Submodel {
  const { submodels } = store.useSnapshot(docId);
  return submodels.find((s) => s.id === id) ?? blankSubmodel(id);
}
